import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { alpha } from '@mui/material/styles';

import SearchRounded from '@mui/icons-material/SearchRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined';
import StarRounded from '@mui/icons-material/StarRounded';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import LoginOutlined from '@mui/icons-material/LoginOutlined';

import PageHeader from '../components/common/PageHeader';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getBankerDetail, addBankerTeamMember, removeBankerTeamMember } from '../services/userService';
import { STATUS_COLORS } from '../config/constants';

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
};

const InfoItem = ({ icon, label, value, copyable }) => {
  if (!value) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: '#9CA3AF', mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#1F2937', fontWeight: 500 }}>
            {value}
          </Typography>
          {copyable && (
            <Tooltip title="Copy">
              <IconButton size="small" onClick={handleCopy} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                <ContentCopyOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const StatCard = ({ label, value, color, icon, subtitle }) => (
  <Paper sx={{ px: 3, py: 2.5, flex: 1, minWidth: 160, position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', top: 12, right: 16, color: alpha(color, 0.15) }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: 0.25 }}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const BankerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [banker, setBanker] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [students, setStudents] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalLoanAmount, setTotalLoanAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('assignedToBankerAt');
  const [sortDir, setSortDir] = useState('desc');

  // Add team member dialog
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', email: '', phone: '' });
  const [teamFormError, setTeamFormError] = useState('');
  const [teamFormLoading, setTeamFormLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getBankerDetail(id);
      const d = res.data?.data;
      setBanker(d?.banker || null);
      setTeamMembers(d?.teamMembers || []);
      setStudents(d?.students || []);
      setStatusCounts(d?.statusCounts || {});
      setTotalLoanAmount(d?.totalLoanAmount || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load banker details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysSince = (date) => {
    if (!date) return '-';
    return Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  };

  // --- Filtered & Sorted Students ---
  const filteredStudents = useMemo(() => {
    let result = [...students];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.fileNumber?.toLowerCase().includes(q) ||
          s.course?.toLowerCase().includes(q) ||
          s.country?.toLowerCase().includes(q)
      );
    }

    if (filterStatus !== 'all') {
      result = result.filter((s) => s.status === filterStatus);
    }

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'loanAmount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [students, search, filterStatus, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // --- Team Member Management ---
  const handleOpenTeamDialog = () => {
    setTeamForm({ name: '', email: '', phone: '' });
    setTeamFormError('');
    setTeamDialogOpen(true);
  };

  const handleAddTeamMember = async () => {
    if (!teamForm.name.trim() || !teamForm.email.trim()) {
      setTeamFormError('Name and email are required');
      return;
    }
    try {
      setTeamFormLoading(true);
      setTeamFormError('');
      const res = await addBankerTeamMember(id, teamForm);
      const newMember = res.data?.data?.teamMember;
      if (newMember) {
        setTeamMembers((prev) => [...prev, newMember]);
      }
      setTeamDialogOpen(false);
      setSnackbar({ open: true, message: 'Team member added successfully', severity: 'success' });
    } catch (err) {
      setTeamFormError(err.response?.data?.message || 'Failed to add team member');
    } finally {
      setTeamFormLoading(false);
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!window.confirm('Deactivate this team member? They will no longer be able to login.')) return;
    try {
      await removeBankerTeamMember(memberId);
      setTeamMembers((prev) =>
        prev.map((m) => m._id === memberId ? { ...m, isActive: false } : m)
      );
      setSnackbar({ open: true, message: 'Team member deactivated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to deactivate', severity: 'error' });
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!banker) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Banker not found</Alert>
      </Box>
    );
  }

  const activeTeamCount = teamMembers.filter((m) => m.isActive !== false).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <PageHeader
        title={banker.name}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Bankers', link: '/bankers' },
          { label: banker.name },
        ]}
      >
        {!banker.isActive && (
          <Chip label="Inactive" size="small" sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600 }} />
        )}
        {banker.isActive && (
          <Chip label="Active" size="small" sx={{ bgcolor: alpha('#10B981', 0.1), color: '#10B981', fontWeight: 600 }} />
        )}
        <Button
          variant="outlined"
          startIcon={<EditOutlined />}
          onClick={() => navigate(`/users/${banker._id}/edit`)}
          sx={{ textTransform: 'none' }}
        >
          Edit Banker
        </Button>
      </PageHeader>

      {/* Stat Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label="Total Students"
          value={students.length}
          color="#2C7BE5"
          icon={<SchoolOutlined sx={{ fontSize: 40 }} />}
          subtitle={`${Object.keys(statusCounts).length} different statuses`}
        />
        <StatCard
          label="Total Loan Amount"
          value={formatAmount(totalLoanAmount)}
          color="#F59E0B"
          icon={<TrendingUpOutlined sx={{ fontSize: 40 }} />}
          subtitle={`${students.length} files`}
        />
        <StatCard
          label="Team Members"
          value={activeTeamCount}
          color="#1A73E8"
          icon={<GroupsRounded sx={{ fontSize: 40 }} />}
          subtitle={teamMembers.length > activeTeamCount ? `${teamMembers.length - activeTeamCount} inactive` : 'All active'}
        />
        <StatCard
          label="Bank"
          value={banker.bankName || '-'}
          color="#6B7280"
          icon={<AccountBalanceOutlined sx={{ fontSize: 40 }} />}
          subtitle={banker.bankBranch || ''}
        />
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ px: 2, borderBottom: '1px solid #E5E7EB' }}
        >
          <Tab label={`Students (${students.length})`} />
          <Tab label={`Team Members (${teamMembers.length})`} />
          <Tab label="Banker Details" />
        </Tabs>

        {/* Tab 0: Students */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Search & Status Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ color: '#9CA3AF', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { backgroundColor: '#F9FAFB' } }}
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`All (${students.length})`}
                  onClick={() => setFilterStatus('all')}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: filterStatus === 'all' ? alpha('#2C7BE5', 0.15) : '#F3F4F6',
                    color: filterStatus === 'all' ? '#2C7BE5' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
                {Object.entries(statusCounts).map(([status, count]) => (
                  <Chip
                    key={status}
                    label={`${status} (${count})`}
                    onClick={() => setFilterStatus(status)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      bgcolor: filterStatus === status ? alpha(STATUS_COLORS[status] || '#9CA3AF', 0.15) : '#F3F4F6',
                      color: filterStatus === status ? (STATUS_COLORS[status] || '#6B7280') : '#6B7280',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Students Table */}
            {filteredStudents.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#9CA3AF' }}>
                  {search || filterStatus !== 'all' ? 'No students match your filters' : 'No students assigned to this banker'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'fileNumber'} direction={sortField === 'fileNumber' ? sortDir : 'asc'} onClick={() => handleSort('fileNumber')}>
                          File No.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                          Student Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Course / Country</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'loanAmount'} direction={sortField === 'loanAmount' ? sortDir : 'asc'} onClick={() => handleSort('loanAmount')}>
                          Loan Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'assignedToBankerAt'} direction={sortField === 'assignedToBankerAt' ? sortDir : 'asc'} onClick={() => handleSort('assignedToBankerAt')}>
                          Days
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.map((student) => (
                      <TableRow
                        key={student._id}
                        hover
                        sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                        onClick={() => navigate(`/students/${student._id}`)}
                      >
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2C7BE5' }}>
                          {student.fileNumber || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#1F2937', fontWeight: 500 }}>
                          {student.name}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#1F2937' }}>{student.course || '-'}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{student.country || '-'}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1F2937' }}>
                          {formatAmount(student.loanAmount)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={student.status} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                          {getDaysSince(student.assignedToBankerAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {filteredStudents.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  Showing {filteredStudents.length} of {students.length} students
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1F2937' }}>
                  Total Loan: <span style={{ color: '#F59E0B' }}>{formatAmount(filteredStudents.reduce((s, st) => s + (st.loanAmount || 0), 0))}</span>
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab 1: Team Members */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  icon={<StarRounded sx={{ fontSize: '14px !important', color: '#D97706 !important' }} />}
                  label={`Primary: ${banker.name}`}
                  size="small"
                  sx={{ fontSize: '0.75rem', bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }}
                />
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  {activeTeamCount} active member{activeTeamCount !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<GroupAddOutlined sx={{ fontSize: 16 }} />}
                onClick={handleOpenTeamDialog}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#1A73E8',
                  '&:hover': { bgcolor: '#1557B0' },
                }}
              >
                Add Team Member
              </Button>
            </Box>

            {teamMembers.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderColor: '#E5E7EB' }}
              >
                <GroupsRounded sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
                <Typography sx={{ fontSize: '0.9rem', color: '#6B7280', mb: 0.5 }}>
                  No team members yet
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                  Add team members who can access this banker's assigned student files.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {teamMembers.map((member) => (
                  <Paper
                    key={member._id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      minWidth: 260,
                      maxWidth: 320,
                      flex: '1 1 260px',
                      borderColor: member.isActive !== false ? '#E5E7EB' : '#FECACA',
                      opacity: member.isActive !== false ? 1 : 0.6,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Avatar sx={{
                        width: 40, height: 40, fontSize: '0.85rem', fontWeight: 700,
                        bgcolor: member.isActive !== false ? alpha('#1A73E8', 0.15) : '#E5E7EB',
                        color: member.isActive !== false ? '#1A73E8' : '#9CA3AF',
                      }}>
                        {member.name?.charAt(0)?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1F2937' }}>
                            {member.name}
                          </Typography>
                          {member.isActive === false && (
                            <Chip label="Inactive" size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{member.email}</Typography>
                      </Box>
                      {member.isActive !== false && (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTeamMember(member._id)}
                            sx={{ color: '#9CA3AF', '&:hover': { color: '#EF4444', bgcolor: alpha('#EF4444', 0.08) } }}
                          >
                            <PersonRemoveOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {member.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneOutlined sx={{ fontSize: 14, color: '#9CA3AF' }} />
                          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{member.phone}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeOutlined sx={{ fontSize: 14, color: '#9CA3AF' }} />
                        <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                          Added {formatDate(member.createdAt)}
                        </Typography>
                      </Box>
                      {member.lastLogin && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LoginOutlined sx={{ fontSize: 14, color: '#9CA3AF' }} />
                          <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                            Last login {formatDate(member.lastLogin)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: Banker Details */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, border: '1px solid #E5E7EB' }} elevation={0}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                    Contact Information
                  </Typography>
                  <InfoItem icon={<PersonOutlined sx={{ fontSize: 20 }} />} label="Full Name" value={banker.name} />
                  <InfoItem icon={<EmailOutlined sx={{ fontSize: 20 }} />} label="Email" value={banker.email} copyable />
                  <InfoItem icon={<PhoneOutlined sx={{ fontSize: 20 }} />} label="Phone" value={banker.phone} copyable />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, border: '1px solid #E5E7EB' }} elevation={0}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceOutlined sx={{ fontSize: 18 }} /> Bank Details
                  </Typography>
                  <InfoItem icon={<AccountBalanceOutlined sx={{ fontSize: 20 }} />} label="Bank Name" value={banker.bankName} />
                  <InfoItem icon={<AccountBalanceOutlined sx={{ fontSize: 20 }} />} label="Branch" value={banker.bankBranch} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Box sx={{ color: '#9CA3AF', mt: 0.25 }}><CheckCircleOutline sx={{ fontSize: 20 }} /></Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                        Status
                      </Typography>
                      <Chip
                        label={banker.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontWeight: 600,
                          bgcolor: banker.isActive ? alpha('#10B981', 0.1) : alpha('#EF4444', 0.1),
                          color: banker.isActive ? '#10B981' : '#EF4444',
                        }}
                      />
                    </Box>
                  </Box>
                  <InfoItem icon={<AccessTimeOutlined sx={{ fontSize: 20 }} />} label="Created" value={formatDate(banker.createdAt)} />
                  {banker.lastLogin && (
                    <InfoItem icon={<LoginOutlined sx={{ fontSize: 20 }} />} label="Last Login" value={formatDate(banker.lastLogin)} />
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>

      {/* Add Team Member Dialog */}
      <Dialog
        open={teamDialogOpen}
        onClose={() => setTeamDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          Add Team Member
          <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 400, mt: 0.25 }}>
            Under {banker.name} ({banker.bankName})
          </Typography>
        </DialogTitle>
        <DialogContent>
          {teamFormError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{teamFormError}</Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            size="small"
            value={teamForm.name}
            onChange={(e) => setTeamForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Email (Google account)"
            size="small"
            type="email"
            value={teamForm.email}
            onChange={(e) => setTeamForm((f) => ({ ...f, email: e.target.value }))}
            sx={{ mb: 2 }}
            required
            helperText="Team member will sign in with this Google email"
          />
          <TextField
            fullWidth
            label="Phone (optional)"
            size="small"
            value={teamForm.phone}
            onChange={(e) => setTeamForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTeamDialogOpen(false)} sx={{ color: '#6B7280', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTeamMember}
            disabled={teamFormLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#1A73E8',
              '&:hover': { bgcolor: '#1557B0' },
            }}
          >
            {teamFormLoading ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BankerDetailPage;
